import {inject} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest} from '@angular/common/http';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, filter, switchMap, take} from 'rxjs/operators';
import {CommonService} from '../services/common.service';

// Global variables for token refresh state
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

// Define token configurations for different endpoints
const tokenConfigs = [
  {
    urlPattern: 'verify-email',
    tokenKey: 'accesshub/verify-email-token'
  },
   {
    urlPattern: 'resend',
    tokenKey: 'accesshub/verify-email-token'
  },
  {
    urlPattern: 'password/set',
    tokenKey: 'accesshub/reset-password-token'
  },
  {
    urlPattern: 'password/otp/validate',
    tokenKey: 'accesshub/reset-password-token'
  },
  
  {
    urlPattern: 'signup',
    tokenKey: 'accesshub/invite-token'
  }
];

const skipRefreshTokenUrls = [
  'signup',
  'login',
  'password/forgot',
  'password/otp/validate',
  'password/set',
  'verify-email'
]

const DEFAULT_TOKEN_KEY = 'accesshub/token';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const commonService = inject(CommonService);

  const tokenKey = getTokenKeyForUrl(req.url);
  const token = commonService.getItemFromLocalStorage(tokenKey);

  if (token) {
    req = addTokenToRequest(req, token);
  }

  return next(req).pipe(
    catchError((response: HttpErrorResponse) => {
      if (response.status === 401) {
        const shouldSkip = shouldSkipTokenRefresh(req.url);

        if (shouldSkip) {
          return throwError(() => {
            commonService.notification(response?.error?.message || 'Oops! Something went wrong', 'danger');
            return response
          });
        }

        return handleTokenRefresh(req, next, commonService);
      }
      return throwError(() => {
        commonService.notification(response?.error?.message || 'Oops! Something went wrong', 'danger');
        return response;
      });
    })
  );
};

const getTokenKeyForUrl = (url: string): string => {
  const config = tokenConfigs.find(config => url.includes(config.urlPattern));
  return config ? config.tokenKey : DEFAULT_TOKEN_KEY;
};

const addTokenToRequest = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
};

const handleTokenRefresh = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  commonService: CommonService
): Observable<HttpEvent<unknown>> => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = commonService.getItemFromLocalStorage('accesshubrefresh-token');
    if (!refreshToken) {
      isRefreshing = false;
      return throwError(() => new Error('No refresh token available'));
    }

    return commonService.refreshToken().pipe(
      switchMap(response => {
        isRefreshing = false;

        const newToken = response.accessToken;
        refreshTokenSubject.next(newToken);

        const clonedRequest = addTokenToRequest(request, newToken);
        return next(clonedRequest);
      }),
      catchError(error => {
        isRefreshing = false;
        commonService.logout();
        return throwError(() => error);
      })
    );
  } else {
    // Wait for the token to be refreshed
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        const clonedRequest = addTokenToRequest(request, token!);
        return next(clonedRequest);
      })
    );
  }
};

const shouldSkipTokenRefresh = (url: string): boolean => {
  const matchedPattern = skipRefreshTokenUrls.find(pattern => url.includes(pattern));

  if (matchedPattern) {
    return true;
  }

  return false;
};
